/**
 * Voice Memo Composer Preview
 *
 * Compact voice memo draft shown inside the chat input.
 *
 * Primary responsibilities:
 * - Show live recording and transcribing state
 * - Show final duration and waveform before send
 * - Let users discard the current voice memo draft
 *
 * Dependencies:
 * - components/messages/voice-memo-waveform
 */

'use client'

import { Loader2, Mic, Pause, Pencil, Volume2, X } from 'lucide-react'
import { useEffect, useRef, useState, type FormEvent } from 'react'

import {
  formatVoiceMemoDuration,
  VoiceMemoWaveform
} from '@/components/messages/voice-memo-waveform'
import { cn } from '@/lib/utils'

interface VoiceMemoComposerPreviewProps {
  audioUrl?: string | null
  contentType?: string | null
  durationSeconds: number | null
  isRecording?: boolean
  isTranscribing?: boolean
  /** Live dictation text shown while recording (self-corrects). */
  liveTranscript?: string
  onDiscard: () => void
  onTranscriptChange?: (transcript: string) => void
  samples: number[]
  transcript?: string
}

// --------------

export function VoiceMemoComposerPreview({
  audioUrl,
  contentType,
  durationSeconds,
  isRecording = false,
  isTranscribing = false,
  liveTranscript,
  onDiscard,
  onTranscriptChange,
  samples,
  transcript
}: VoiceMemoComposerPreviewProps) {
  const label = isRecording
    ? 'Recording'
    : isTranscribing
      ? 'Transcribing'
      : 'Voice note ready'
  const cleanTranscript = transcript?.trim()
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isEditingTranscript, setIsEditingTranscript] = useState(false)
  const [transcriptDraft, setTranscriptDraft] = useState(cleanTranscript ?? '')
  const canPlayAudio = Boolean(audioUrl && !isRecording && !isTranscribing)
  const canEditTranscript = Boolean(onTranscriptChange && !isRecording && !isTranscribing)

  useEffect(() => {
    setIsPlaying(false)
  }, [audioUrl])

  useEffect(() => {
    if (!isEditingTranscript) {
      setTranscriptDraft(cleanTranscript ?? '')
    }
  }, [cleanTranscript, isEditingTranscript])

  const saveTranscriptDraft = (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault()
    const nextTranscript = transcriptDraft.trim()
    onTranscriptChange?.(nextTranscript)
    setTranscriptDraft(nextTranscript)
    setIsEditingTranscript(false)
  }

  const toggleAudioPlayback = () => {
    const audio = audioRef.current

    if (!audio) {
      return
    }

    if (audio.paused) {
      void audio.play().catch(() => setIsPlaying(false))
      return
    }

    audio.pause()
  }

  return (
    <div className="px-2 pt-2">
      <div
        aria-label={label}
        className={cn(
          'border-border/70 bg-background/95 flex min-w-0 items-center gap-3 rounded-full border px-4 py-2.5 shadow-sm',
          isRecording && 'border-red-500/40 bg-red-500/5'
        )}
      >
        <span className="sr-only">{label}</span>
        {canPlayAudio ? (
          <div className="size-12 shrink-0 sm:size-14">
            <audio
              key={audioUrl ?? 'voice-memo-draft'}
              ref={audioRef}
              className="sr-only"
              preload="metadata"
              onEnded={() => setIsPlaying(false)}
              onPause={() => setIsPlaying(false)}
              onPlay={() => setIsPlaying(true)}
            >
              <source
                src={audioUrl ?? undefined}
                {...(contentType ? { type: contentType } : {})}
              />
            </audio>
            <button
              type="button"
              className="bg-muted text-foreground hover:bg-accent focus-visible:ring-ring flex size-12 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 sm:size-14"
              aria-label={isPlaying ? 'Pause voice note' : 'Listen to voice note'}
              onClick={toggleAudioPlayback}
            >
              {isPlaying ? (
                <Pause className="size-5 sm:size-6" aria-hidden="true" />
              ) : (
                <Volume2 className="size-5 sm:size-6" aria-hidden="true" />
              )}
            </button>
          </div>
        ) : (
          <div className="bg-muted text-foreground flex size-12 shrink-0 items-center justify-center rounded-full sm:size-14">
            {isTranscribing ? (
              <Loader2 className="size-5 animate-spin sm:size-6" aria-hidden="true" />
            ) : (
              <Mic
                className={cn('size-6 sm:size-7', isRecording && 'animate-pulse text-red-500')}
                aria-hidden="true"
              />
            )}
          </div>
        )}
        <div className="min-w-0 flex-1 overflow-hidden">
          <VoiceMemoWaveform
            active={isRecording}
            className="h-9"
            samples={samples}
          />
        </div>
        <span className="text-foreground w-12 shrink-0 text-right text-lg tabular-nums sm:w-14 sm:text-2xl">
          {formatVoiceMemoDuration(durationSeconds)}
        </span>
        <button
          type="button"
          className="text-foreground/80 hover:bg-accent hover:text-foreground focus-visible:ring-ring flex size-9 shrink-0 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 sm:size-10"
          aria-label="Discard voice note"
          onClick={onDiscard}
        >
          <X className="size-6" aria-hidden="true" />
        </button>
      </div>
      {isRecording && liveTranscript?.trim() ? (
        // Live dictation: self-correcting transcript while recording.
        <p
          aria-live="polite"
          aria-label="Live transcript"
          className="text-muted-foreground line-clamp-3 pl-[5.5rem] pr-4 pt-3 text-sm leading-relaxed"
        >
          {liveTranscript.trim()}
        </p>
      ) : null}
      {!isRecording && !isTranscribing ? (
        <div className="flex items-start gap-2 pl-[5.5rem] pr-4 pt-3">
          {isEditingTranscript ? (
            <form className="min-w-0 flex-1 space-y-2" onSubmit={saveTranscriptDraft}>
              <textarea
                aria-label="Edit voice note transcript"
                autoFocus
                className="border-border bg-background text-foreground focus-visible:ring-ring min-h-20 w-full resize-none rounded-xl border px-3 py-2 text-sm leading-relaxed shadow-sm focus-visible:outline-none focus-visible:ring-2 sm:text-base"
                value={transcriptDraft}
                onChange={(event) => setTranscriptDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    setTranscriptDraft(cleanTranscript ?? '')
                    setIsEditingTranscript(false)
                  }
                }}
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring rounded-lg px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2"
                  onClick={() => {
                    setTranscriptDraft(cleanTranscript ?? '')
                    setIsEditingTranscript(false)
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:ring-ring rounded-lg px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2"
                >
                  Save
                </button>
              </div>
            </form>
          ) : (
            <>
              <p
                aria-label="Voice note transcript preview"
                className="text-muted-foreground/80 line-clamp-2 min-w-0 flex-1 cursor-default select-none text-sm italic leading-relaxed sm:text-base"
                role="note"
                onMouseDownCapture={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                }}
              >
                {cleanTranscript || 'No transcript captured yet.'}
              </p>
              {canEditTranscript ? (
                <button
                  type="button"
                  aria-label="Edit voice note transcript"
                  className="text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring -mr-1 flex size-8 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2"
                  onClick={() => setIsEditingTranscript(true)}
                >
                  <Pencil className="size-4" aria-hidden="true" />
                </button>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  )
}
